<template>
  <div class="excel-mapper-container">
    <h3>Excel Configuration</h3>
    
    <!-- Consistency Analysis Summary -->
    <div v-if="consistencyAnalysis" class="consistency-summary-banner">
      <h4>📊 Excel Format Analysis Results</h4>
      <div class="consistency-stats">
        <div class="stat-item success">
          <span class="stat-number">{{ consistencyAnalysis.consistent_files }}</span>
          <span class="stat-label">Consistent Files (using this config)</span>
        </div>
        <div v-if="consistencyAnalysis.inconsistent_files > 0" class="stat-item warning">
          <span class="stat-number">{{ consistencyAnalysis.inconsistent_files }}</span>
          <span class="stat-label">Files needing individual setup</span>
        </div>
      </div>
      <div class="consistency-explanation">
        <p v-if="consistencyAnalysis.consistent_files > 0">
          ✅ <strong>This configuration will be applied to {{ consistencyAnalysis.consistent_files }} files</strong> with consistent column structures.
        </p>
        <p v-if="consistencyAnalysis.inconsistent_files > 0" class="warning-text">
          ⚠️ <strong>{{ consistencyAnalysis.inconsistent_files }} files have different formats</strong> and will need individual template setup via Project Documents.
        </p>
      </div>
    </div>
    
    <div class="mapping-info">
      <p>Configure how to extract data from your Excel files. This configuration will be applied to all Excel files in your project.</p>
    </div>
    
    <div class="sample-file-info">
      <p><strong>Sample File:</strong> {{ sampleFileName }}</p>
      <p><strong>Total Excel Files:</strong> {{ projectImport.scan_results?.excel_analysis?.total_excel_files || 0 }}</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-section">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Loading Excel data...</p>
      </div>
    </div>

    <!-- Fallback for when Excel data is not available -->
    <div v-if="!isLoading && !excelData" class="excel-preview-fallback">
      <h4>Excel Preview & Mapping</h4>
      <div class="placeholder-message">
        <p>⚠️ Unable to load Excel preview data</p>
        <p>Using quick configuration mode instead</p>
      </div>
      
      <!-- Quick configuration form -->
      <div class="mapping-form">
        <h4>Quick Configuration</h4>
        <div class="form-row">
          <label>Header Row:</label>
          <input type="number" v-model="quickConfig.header_row" min="1" />
        </div>
        <div class="form-row">
          <label>Data Start Row:</label>
          <input type="number" v-model="quickConfig.data_row" min="1" />
        </div>
        <div class="form-row">
          <label>Description Column:</label>
                      <input type="text" v-model="quickConfig.description_columns[0]" placeholder="A, B, C..." />
        </div>
        <div class="form-row">
          <label>Budget Column:</label>
          <input type="text" v-model="quickConfig.budget_column" placeholder="A, B, C..." />
        </div>
      </div>
      
      <div class="actions">
        <button @click="testMapping" class="btn-secondary">Test Mapping</button>
        <button @click="saveQuickConfiguration" class="btn-primary">Save & Continue</button>
      </div>
    </div>

    <!-- Excel Configuration Flow -->
    <div v-if="!isLoading && excelData" class="excel-config-section">
      <!-- Current Step Display -->
      <div class="current-step">
        <h4>{{ currentStepTitle }}</h4>
        <p>{{ currentStepDescription }}</p>
      </div>

      <!-- Header Row Selection -->
      <div v-if="currentStep === 'header_selection'" class="header-selection-step">
        <div class="excel-preview">
          <p>Click on the row that contains the column headers (Budget, Draw periods, etc.)</p>
          <table class="raw-excel-table">
            <tbody>
              <tr v-for="(row, rowIndex) in excelData.rows.slice(0, 20)" :key="rowIndex"
                  :class="{ 'selected-header-row': selectedHeaderRow === rowIndex }"
                  @click="selectHeaderRow(rowIndex)">
                <td class="row-number">{{ rowIndex + 1 }}</td>
                <td v-for="(cell, cellIndex) in row" :key="cellIndex" class="excel-cell">
                  {{ formatCellValue(cell) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Description Structure Choice -->
      <div v-if="currentStep === 'description_structure'" class="description-structure-step">
        <div class="structure-choice">
          <div class="choice-options">
            <div class="choice-option" 
                 :class="{ selected: !useHierarchicalDescriptions }"
                 @click="useHierarchicalDescriptions = false">
              <h5>Single Description Column</h5>
              <p>All descriptions are in one column</p>
              <div class="example">Example: "Design Architect", "Interior Design", "Foundation Work"</div>
            </div>
            
            <div class="choice-option" 
                 :class="{ selected: useHierarchicalDescriptions }"
                 @click="useHierarchicalDescriptions = true">
              <h5>Hierarchical Descriptions</h5>
              <p>Separate columns for main categories and specific descriptions</p>
              <div class="example">
                <strong>Main Category:</strong> "Architecture/Engineering"<br>
                <strong>Description:</strong> "Design Architect"
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Column Selection Steps -->
      <div v-if="['main_category_column', 'description_columns', 'single_description_columns', 'original_budget_column', 'current_budget_column', 'funded_at_closing_column', 'current_draw_column', 'balance_to_complete_column', 'draw_columns', 'contractor_column'].includes(currentStep)" class="column-selection-step">
        <div class="excel-preview">
          <table class="raw-excel-table">
            <tbody>
              <tr v-for="(row, rowIndex) in excelData.rows.slice(0, 20)" :key="rowIndex"
                  :class="{ 'selected-header-row': selectedHeaderRow === rowIndex }">
                <td class="row-number">{{ rowIndex + 1 }}</td>
                <td v-for="(cell, cellIndex) in row" :key="cellIndex" 
                    class="excel-cell"
                    :class="getColumnSelectionHighlight(cellIndex)"
                    @click="selectColumn(cellIndex)">
                  {{ formatCellValue(cell) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Date Row Selection Step -->
      <div v-if="currentStep === 'date_row_selection'" class="date-row-selection-step">
        <div class="excel-preview">
          <table class="raw-excel-table">
            <tbody>
              <tr v-for="(row, rowIndex) in excelData.rows.slice(0, 20)" :key="rowIndex"
                  :class="{ 
                    'selected-header-row': selectedHeaderRow === rowIndex,
                    'selected-date-row': selectedDateRow === rowIndex,
                    'clickable-row': rowIndex !== selectedHeaderRow
                  }"
                  @click="selectDateRow(rowIndex)">
                <td class="row-number">{{ rowIndex + 1 }}</td>
                <td v-for="(cell, cellIndex) in row" :key="cellIndex" 
                    class="excel-cell"
                    :class="{
                      'highlighted-draw-column': isDateRowColumn(cellIndex)
                    }">
                  {{ formatCellValue(cell) }}
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="selectedDateRow !== null" class="date-row-info">
            <p><strong>Selected Date Row:</strong> Row {{ selectedDateRow + 1 }}</p>
            <p>The system will extract dates/periods from this row to track draw history.</p>
          </div>
        </div>
      </div>

      <!-- DDA Term Mapping Step -->
      <div v-if="currentStep === 'dda_term_mapping'" class="dda-term-mapping-step">
        <div class="mapping-interface">
          <div class="dda-mapping-table-container">
            <table class="dda-mapping-table">
              <thead>
                <tr>
                  <th class="dda-term-header">DDA Term</th>
                  <th class="description-header">Description of Work</th>
                  <th class="company-header">Company</th>
                  <th class="budget-header">Original Budget</th>
                  <th class="budget-header">Current Budget</th>
                  <th class="billing-header">Current Draw</th>
                  <th class="percent-header">Complete %</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in classificationItems" :key="index"
                    :class="{ 'selected-row': selectedExcelItem === index }"
                    @click="selectExcelItem(index)">
                  <td class="dda-term-cell">
                    <div class="dda-term-wrapper">
                      <span v-if="item.auto_matched" class="auto-match-badge">AUTO</span>
                      <div class="dda-autocomplete" :class="{ 'auto-matched': item.auto_matched }">
                        <input 
                          type="text" 
                          v-model="item.searchTerm"
                          :placeholder="item.dda_term_id ? getSelectedTermName(item.dda_term_id) : 'Search DDA Terms...'"
                          @input="searchDDATerms(item, $event.target.value)"
                          @focus="handleFocus(item)"
                          @blur="handleBlur(item)"
                          class="dda-search-input"
                          :class="{ 'has-selection': item.dda_term_id, 'auto-matched': item.auto_matched }"
                        />
                        <div v-if="item.showDropdown && item.searchResults" class="dda-dropdown">
                          <div v-if="item.searchResults.length === 0" class="no-results">
                            No matching DDA terms found
                          </div>
                          <div 
                            v-for="term in item.searchResults.slice(0, 10)" 
                            :key="term.id"
                            class="dda-option"
                            @mousedown="selectDDATermForItem(item, term)"
                            :class="{ 'selected': item.dda_term_id === term.id }"
                          >
                            <div class="term-name">{{ term.term }}</div>
                            <div v-if="term.category_name" class="term-category">{{ term.category_name }}</div>
                          </div>
                        </div>
                        <div v-if="item.dda_term_id" class="selected-term-display">
                          <span class="selected-term-name">{{ getSelectedTermName(item.dda_term_id) }}</span>
                          <button @click="clearSelection(item)" class="clear-selection" type="button">×</button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="description-cell">{{ item.description }}</td>
                  <td>{{ item.company || '' }}</td>
                  <td class="currency">{{ formatCurrency(item.original_budget) }}</td>
                  <td class="currency">{{ formatCurrency(item.current_budget) }}</td>
                  <td class="currency">{{ formatCurrency(item.current_billing) }}</td>
                  <td class="percent">{{ formatPercent(item.complete_percent) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="mapping-progress">
            <div class="progress-info">
              <strong>Classification Progress:</strong> 
              {{ classifiedCount }} of {{ classificationItems.length }} items shown 
              ({{ Math.round(classificationProgress) }}%)
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: classificationProgress + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Finalize Step - Show Summary -->
      <div v-if="currentStep === 'finalize'" class="finalize-step">
        <div class="mapping-summary">
          <h4>Column Mapping Summary</h4>
          <p>Review your column selections:</p>
          
          <div class="summary-list">
            <div v-if="useHierarchicalDescriptions" class="summary-item">
              <strong>Main Category Column:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.main_category_column !== null }">
                {{ excelMapping.main_category_column !== null ? `Column ${String.fromCharCode(65 + excelMapping.main_category_column)}` : 'Not selected' }}
              </span>
            </div>
            <div class="summary-item">
              <strong>{{ useHierarchicalDescriptions ? 'Description Columns:' : 'Description Columns:' }}</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.description_columns.length > 0 }">
                <span v-if="excelMapping.description_columns.length > 0">
                  {{ excelMapping.description_columns.map(col => String.fromCharCode(65 + col)).join(', ') }}
                </span>
                <span v-else>Not selected</span>
              </span>
            </div>
            <div class="summary-item">
              <strong>Original Budget Column:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.original_budget_column !== null }">
                {{ excelMapping.original_budget_column !== null ? `Column ${String.fromCharCode(65 + excelMapping.original_budget_column)}` : 'Not selected' }}
              </span>
            </div>
            <div class="summary-item">
              <strong>Current Budget Column:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.current_budget_column !== null }">
                {{ excelMapping.current_budget_column !== null ? `Column ${String.fromCharCode(65 + excelMapping.current_budget_column)}` : 'Not selected' }}
              </span>
            </div>
            <div class="summary-item">
              <strong>Funded at Closing Column:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.funded_at_closing_column !== null }">
                {{ excelMapping.funded_at_closing_column !== null ? `Column ${String.fromCharCode(65 + excelMapping.funded_at_closing_column)}` : 'Not selected (Optional)' }}
              </span>
            </div>
            <div class="summary-item">
              <strong>Current Draw Column:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.current_draw_column !== null }">
                {{ excelMapping.current_draw_column !== null ? `Column ${String.fromCharCode(65 + excelMapping.current_draw_column)}` : 'Not selected' }}
              </span>
            </div>
            <div class="summary-item">
              <strong>Draw Columns:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.draw_columns.length > 0 }">
                <span v-if="excelMapping.draw_columns.length > 0">
                  {{ excelMapping.draw_columns.length }} columns selected
                </span>
                <span v-else>Not selected</span>
              </span>
            </div>
            <div class="summary-item">
              <strong>Balance to Complete Column:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.balance_to_complete_column !== null }">
                {{ excelMapping.balance_to_complete_column !== null ? `Column ${String.fromCharCode(65 + excelMapping.balance_to_complete_column)}` : 'Not selected (Optional)' }}
              </span>
            </div>
            <div class="summary-item">
              <strong>Date/Period Row:</strong> 
              <span class="column-mapping" :class="{ mapped: selectedDateRow !== null }">
                {{ selectedDateRow !== null ? `Row ${selectedDateRow + 1}` : 'Not selected' }}
              </span>
            </div>
            <div class="summary-item">
              <strong>Contractor/Company Column:</strong> 
              <span class="column-mapping" :class="{ mapped: excelMapping.company_column !== null }">
                {{ excelMapping.company_column !== null ? `Column ${String.fromCharCode(65 + excelMapping.company_column)}` : 'Not selected (Optional)' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <button v-if="currentStep !== firstStepKey" 
              class="btn-secondary" 
              @click="previousStep">
        Previous Step
      </button>
      <button v-if="!isLastStep" 
              class="btn-primary" 
              :disabled="!canProceed"
              @click="nextStep">
        Next Step
      </button>
      <button v-if="isLastStep" 
              class="btn-success" 
              :disabled="!canProceed"
              @click="saveConfiguration">
        Save & Continue
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'

const props = defineProps({
  projectImport: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['complete'])

// State
const currentStep = ref('header_selection')
const isLoading = ref(true)
const excelData = ref(null)
const ddaTerms = ref([])

// Excel mapping configuration
const selectedHeaderRow = ref(null)
const useHierarchicalDescriptions = ref(false)
const selectedDateRow = ref(null)
const selectedExcelItem = ref(null)
const classificationItems = ref([])

const excelMapping = ref({
  main_category_column: null,
  description_columns: [], // Now supports multiple description columns
  cost_code_column: null,
  company_column: null,
  original_budget_column: null,
  current_budget_column: null,
  funded_at_closing_column: null, // Money funded before any draws started (reduces original budget)
  current_draw_column: null,      // Current draw/billing amount for this period
  balance_to_complete_column: null, // Remaining work balance (Total Budget - Work Completed to Date)
  draw_columns: []                 // Multiple draw columns for historical tracking (used to compute work completed previous)
})

// Quick config for fallback mode
const quickConfig = ref({
  header_row: 1,
  data_row: 2,
  description_columns: ['A'],
  budget_column: 'B'
})

// Step Configuration
const steps = [
  {
    key: 'header_selection',
    title: 'Step 1: Select Header Row',
    description: 'Click on the row that contains the column headers (Budget, Draw periods, etc.)'
  },
  {
    key: 'description_structure',
    title: 'Step 2: Description Structure',
    description: 'Choose how descriptions are organized in your spreadsheet.'
  },
  {
    key: 'main_category_column',
    title: 'Step 3a: Select Main Category Column',
    description: 'Click on the column with general categories.'
  },
  {
    key: 'description_columns',
    title: 'Step 3b: Select Description Columns',
    description: 'Select ALL columns that contain descriptions (can be multiple for nested hierarchies). Click to select/deselect columns.'
  },
  {
    key: 'single_description_columns',
    title: 'Step 3: Select Description Columns',
    description: 'Select ALL columns that contain item descriptions or work categories (can be multiple for nested hierarchies). Click to select/deselect columns.'
  },
  {
    key: 'original_budget_column',
    title: 'Step 4: Select Original Budget Column',
    description: 'Click on the column that contains the original budget amounts.'
  },
  {
    key: 'current_budget_column',
    title: 'Step 5: Select Current Budget Column',
    description: 'Click on the column that contains the current or revised budget amounts.'
  },
  {
    key: 'funded_at_closing_column',
    title: 'Step 6: Select Funded at Closing Column (Optional)',
    description: 'If available, click on the column that contains money funded BEFORE any draws started (e.g., "Funded at Closing", "Initial Funding"). This reduces the original budget available for draws.'
  },
  {
    key: 'current_draw_column',
    title: 'Step 7: Select Current Draw Column',
    description: 'Click on the column that contains the current draw/billing amount for this specific draw period (e.g., "Current Request", "This Period", "Current Draw").'
  },
  {
    key: 'draw_columns',
    title: 'Step 8: Select All Draw Columns',
    description: 'Select ALL columns that contain historical draw amounts to track draw progression and compute work completed previous. The leftmost column will be considered the earliest draw. Click to select/deselect columns.'
  },
  {
    key: 'balance_to_complete_column',
    title: 'Step 9: Select Balance to Complete Column (Optional)',
    description: 'If available, click on the column that shows remaining work balance (Total Budget - Work Completed to Date). Examples: "Balance to Finish", "Remaining Budget", "Balance to Complete".'
  },
  {
    key: 'date_row_selection',
    title: 'Step 10: Select Date/Period Row',
    description: 'Click on the row that contains the draw dates or period labels.'
  },
  {
    key: 'contractor_column',
    title: 'Step 11: Select Contractor Column (Optional)',
    description: 'If your spreadsheet has a contractor/company column, select it.'
  },
  {
    key: 'dda_term_mapping',
    title: 'Step 12: DDA Term Classification',
    description: 'Map each budget item to the appropriate DDA construction term.'
  },
  {
    key: 'finalize',
    title: 'Step 13: Review and Finalize',
    description: 'Review your mappings and finalize the template configuration.'
  }
]

// Computed properties
const sampleFileName = computed(() => {
  const sampleFile = props.projectImport.scan_results?.excel_analysis?.sample_file
  return sampleFile ? sampleFile.split('/').pop() : 'No Excel files found'
})

const currentStepConfig = computed(() => {
  return steps.find(s => s.key === currentStep.value) || steps[0]
})

const currentStepTitle = computed(() => currentStepConfig.value.title)
const currentStepDescription = computed(() => currentStepConfig.value.description)

const firstStepKey = computed(() => steps[0].key)
const isLastStep = computed(() => currentStep.value === 'finalize')

const canProceed = computed(() => {
  switch (currentStep.value) {
    case 'header_selection':
      return selectedHeaderRow.value !== null
    case 'description_structure':
      return true
    case 'main_category_column':
      return excelMapping.value.main_category_column !== null
    case 'description_columns':
      return excelMapping.value.description_columns.length > 0
    case 'single_description_columns':
      return excelMapping.value.description_columns.length > 0
    case 'original_budget_column':
      return excelMapping.value.original_budget_column !== null
    case 'current_budget_column':
      return excelMapping.value.current_budget_column !== null
    case 'funded_at_closing_column':
      return true // Optional column, always can proceed
    case 'current_draw_column':
      return excelMapping.value.current_draw_column !== null
    case 'draw_columns':
      return excelMapping.value.draw_columns.length > 0 // Need at least one draw column for historical tracking
    case 'balance_to_complete_column':
      return true // Optional column, always can proceed
    case 'date_row_selection':
      return selectedDateRow.value !== null
    case 'contractor_column':
      return true // Optional step
    case 'dda_term_mapping':
      return classificationProgress.value >= 50 // At least 50% mapped
    case 'finalize':
      return true
    default:
      return false
  }
})

const classifiedCount = computed(() => {
  return classificationItems.value.filter(item => item.dda_term_id).length
})

const classificationProgress = computed(() => {
  if (classificationItems.value.length === 0) return 0
  return (classifiedCount.value / classificationItems.value.length) * 100
})

const consistencyAnalysis = computed(() => {
  return props.projectImport.scan_results?.consistency_analysis
})

// Methods
const loadExcelData = async () => {
  try {
    isLoading.value = true
    
    console.log('Attempting to load Excel preview...')
    
    // Load ALL Excel data from API like DrawTemplateSetup does
    const response = await axios.get(`/api/v1/project_imports/${props.projectImport.id}/excel_preview`)
    
    if (response.data.success) {
      // Convert API response to expected format - exactly like DrawTemplateSetup expects
      const rows = response.data.preview_data.map(item => item.data)
      excelData.value = {
        rows: rows,
        raw_excel_data: true
      }
      
      // Use suggested header row if available
      if (response.data.suggested_config?.suggested_header_row !== undefined) {
        selectedHeaderRow.value = response.data.suggested_config.suggested_header_row
      } else {
        autoDetectHeaderRow()
      }
      
      console.log(`Excel data loaded from API: ${rows.length} rows`)
    } else {
      console.error('Failed to load Excel preview:', response.data.error)
      throw new Error(response.data.error)
    }
  } catch (error) {
    console.error('Error loading Excel data:', error)
    // Continue without Excel data - they're not critical for the first steps
    excelData.value = null
  } finally {
    isLoading.value = false
  }
}

const loadDDATerms = async () => {
  try {
    const response = await axios.get('/api/v1/dda_terms')
    ddaTerms.value = response.data || []
  } catch (error) {
    console.error('Error loading DDA terms:', error)
    // Continue without DDA terms - they're not critical for the first steps
    ddaTerms.value = []
  }
}

const autoDetectHeaderRow = () => {
  if (!excelData.value?.rows) return
  
  // Look for common header keywords
  const headerKeywords = ['description', 'budget', 'amount', 'total', 'contractor', 'company', 'item', 'work']
  
  for (let i = 0; i < Math.min(10, excelData.value.rows.length); i++) {
    const row = excelData.value.rows[i]
    const rowText = row.join(' ').toLowerCase()
    
    if (headerKeywords.some(keyword => rowText.includes(keyword))) {
      selectedHeaderRow.value = i
      break
    }
  }
}

const selectHeaderRow = (rowIndex) => {
  selectedHeaderRow.value = rowIndex
}

const selectColumn = (columnIndex) => {
  switch (currentStep.value) {
    case 'main_category_column':
      excelMapping.value.main_category_column = columnIndex
      break
    case 'description_columns':
    case 'single_description_columns': {
      // Toggle selection for description columns
      const idx = excelMapping.value.description_columns.indexOf(columnIndex)
      if (idx > -1) {
        excelMapping.value.description_columns.splice(idx, 1)
      } else {
        excelMapping.value.description_columns.push(columnIndex)
      }
      break
    }
    case 'original_budget_column':
      excelMapping.value.original_budget_column = columnIndex
      break
    case 'current_budget_column':
      excelMapping.value.current_budget_column = columnIndex
      break
    case 'funded_at_closing_column':
      excelMapping.value.funded_at_closing_column = columnIndex
      break
    case 'current_draw_column':
      excelMapping.value.current_draw_column = columnIndex
      break
    case 'balance_to_complete_column':
      excelMapping.value.balance_to_complete_column = columnIndex
      break
    case 'draw_columns': {
      // Toggle selection for draw columns
      const idx = excelMapping.value.draw_columns.indexOf(columnIndex)
      if (idx > -1) {
        excelMapping.value.draw_columns.splice(idx, 1)
      } else {
        excelMapping.value.draw_columns.push(columnIndex)
      }
      break
    }
    case 'contractor_column':
      excelMapping.value.company_column = columnIndex
      break
  }
}

const selectDateRow = (rowIndex) => {
  if (rowIndex !== selectedHeaderRow.value) {
    selectedDateRow.value = rowIndex
  }
}

const selectExcelItem = (index) => {
  selectedExcelItem.value = index
}

const getColumnSelectionHighlight = (columnIndex) => {
  const classes = []
  
  switch (currentStep.value) {
    case 'main_category_column':
      if (excelMapping.value.main_category_column === columnIndex) {
        classes.push('highlight-description')
      }
      break
    case 'description_columns':
    case 'single_description_columns':
      if (excelMapping.value.description_columns && excelMapping.value.description_columns.includes(columnIndex)) {
        classes.push('highlight-description')
      }
      break
    case 'original_budget_column':
      if (excelMapping.value.original_budget_column === columnIndex) {
        classes.push('highlight-original-budget')
      }
      break
    case 'current_budget_column':
      if (excelMapping.value.current_budget_column === columnIndex) {
        classes.push('highlight-current-budget')
      }
      break
    case 'funded_at_closing_column':
      if (excelMapping.value.funded_at_closing_column === columnIndex) {
        classes.push('highlight-funded-at-closing')
      }
      break
    case 'current_draw_column':
      if (excelMapping.value.current_draw_column === columnIndex) {
        classes.push('highlight-current-draw')
      }
      break
    case 'balance_to_complete_column':
      if (excelMapping.value.balance_to_complete_column === columnIndex) {
        classes.push('highlight-balance-complete')
      }
      break
    case 'draw_columns':
      if (excelMapping.value.draw_columns && excelMapping.value.draw_columns.includes(columnIndex)) {
        classes.push('highlight-draw-columns')
      }
      break
    case 'contractor_column':
      if (excelMapping.value.company_column === columnIndex) {
        classes.push('highlight-company')
      }
      break
  }
  
  return classes.join(' ')
}

const isDateRowColumn = (columnIndex) => {
  return excelMapping.value.draw_columns && excelMapping.value.draw_columns.includes(columnIndex)
}

const formatCellValue = (value) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') {
    if (value > 1000) {
      return value.toLocaleString()
    }
    return value.toString()
  }
  return value.toString()
}

const formatCurrency = (value) => {
  if (!value || isNaN(value)) return '$0'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const formatPercent = (value) => {
  if (!value || isNaN(value)) return '0%'
  return `${Math.round(value)}%`
}

const nextStep = async () => {
  const currentIndex = steps.findIndex(s => s.key === currentStep.value)
  if (currentIndex < steps.length - 1) {
    let nextIndex = currentIndex + 1
    
    // Skip steps based on description structure choice
    if (currentStep.value === 'description_structure') {
      if (useHierarchicalDescriptions.value) {
        currentStep.value = 'main_category_column'
      } else {
        currentStep.value = 'single_description_columns'
      }
      return
    }
    
    // Skip hierarchical steps if not using hierarchical descriptions
    if (!useHierarchicalDescriptions.value && steps[nextIndex].key === 'main_category_column') {
      nextIndex++
    }
    if (!useHierarchicalDescriptions.value && steps[nextIndex].key === 'description_columns') {
      nextIndex++
    }
    
    // Skip single description step if using hierarchical
    if (useHierarchicalDescriptions.value && steps[nextIndex].key === 'single_description_columns') {
      nextIndex++
    }
    
    currentStep.value = steps[nextIndex].key
    
    // Prepare data for DDA term mapping step
    if (currentStep.value === 'dda_term_mapping') {
      prepareClassificationItems()
    }
    
    // Auto-save configuration when reaching finalize step
    if (currentStep.value === 'finalize') {
      await saveConfiguration()
    }
  }
}

const previousStep = () => {
  const currentIndex = steps.findIndex(s => s.key === currentStep.value)
  if (currentIndex > 0) {
    let prevIndex = currentIndex - 1
    
    // Handle description structure navigation
    if (currentStep.value === 'single_description_columns' && !useHierarchicalDescriptions.value) {
      currentStep.value = 'description_structure'
      return
    }
    if (currentStep.value === 'main_category_column' && useHierarchicalDescriptions.value) {
      currentStep.value = 'description_structure'
      return
    }
    
    // Skip steps based on description structure choice
    if (!useHierarchicalDescriptions.value && steps[prevIndex].key === 'description_columns') {
      prevIndex--
    }
    if (!useHierarchicalDescriptions.value && steps[prevIndex].key === 'main_category_column') {
      prevIndex--
    }
    if (useHierarchicalDescriptions.value && steps[prevIndex].key === 'single_description_columns') {
      prevIndex--
    }
    
    currentStep.value = steps[prevIndex].key
  }
}

const prepareClassificationItems = async () => {
  if (!excelData.value?.rows || selectedHeaderRow.value === null) return
  
  classificationItems.value = []
  const dataStartRow = selectedHeaderRow.value + 1
  
  // If we only have sample data, try to load full Excel data
  if (excelData.value.rows.length <= 10 && props.projectImport.id) {
    console.log('Loading full Excel data for classification...')
    try {
      const response = await fetch(`/api/v1/project_imports/${props.projectImport.id}/excel_preview`)
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.preview_data) {
          // Update with full data
          excelData.value = {
            rows: data.preview_data.map(row => row.data),
            raw_excel_data: true,
            metadata: excelData.value.metadata
          }
        }
      }
    } catch (error) {
      console.error('Error loading full Excel data:', error)
    }
  }
  
  // Process all rows after header
  for (let i = dataStartRow; i < excelData.value.rows.length; i++) {
    const row = excelData.value.rows[i]
    
    // Skip empty rows - combine all description columns
    const descriptions = []
    excelMapping.value.description_columns.forEach(colIndex => {
      const desc = row[colIndex]
      if (desc && desc.toString().trim() !== '') {
        descriptions.push(desc.toString().trim())
      }
    })
    
    if (descriptions.length === 0) continue
    
    const combinedDescription = descriptions.join(' - ')
    
    const item = {
      description: combinedDescription,
      main_category: useHierarchicalDescriptions.value ? row[excelMapping.value.main_category_column] : null,
      company: excelMapping.value.company_column !== null ? row[excelMapping.value.company_column] : null,
      original_budget: parseFloat(row[excelMapping.value.original_budget_column]) || 0,
      current_budget: parseFloat(row[excelMapping.value.current_budget_column]) || 0,
      current_billing: excelMapping.value.draw_columns.length > 0 
        ? parseFloat(row[excelMapping.value.draw_columns[excelMapping.value.draw_columns.length - 1]]) || 0 
        : 0,
      complete_percent: 0,
      dda_term_id: null,
      searchTerm: '',
      showDropdown: false,
      searchResults: [],
      auto_matched: false
    }
    
    // Calculate completion percentage
    if (item.current_budget > 0) {
      item.complete_percent = (item.current_billing / item.current_budget) * 100
    }
    
    classificationItems.value.push(item)
  }
  
  console.log(`Prepared ${classificationItems.value.length} items for classification`)
  
  // Auto-match DDA terms
  await autoMatchDDATerms()
}

const autoMatchDDATerms = async () => {
  if (classificationItems.value.length === 0) return
  
  console.log('Starting auto-match for DDA terms...')
  
  try {
    // Extract descriptions for classification
    const descriptions = classificationItems.value.map(item => item.description)
    
    // Call the classify endpoint
    const response = await axios.post('/api/v1/dda_terms/classify', {
      descriptions: descriptions
    })
    
    if (response.data && response.data.classifications) {
      // Apply the classifications
      response.data.classifications.forEach((classification, index) => {
        if (classification.dda_term_id && classification.confidence > 0.3) {
          classificationItems.value[index].dda_term_id = classification.dda_term_id
          classificationItems.value[index].searchTerm = classification.dda_term_name
          classificationItems.value[index].auto_matched = true
          classificationItems.value[index].selectedTerm = {
            id: classification.dda_term_id,
            term: classification.dda_term_name
          }
        }
      })
      
      const matchedCount = classificationItems.value.filter(item => item.auto_matched).length
      console.log(`Auto-matched ${matchedCount} out of ${classificationItems.value.length} items`)
    }
  } catch (error) {
    console.error('Error auto-matching DDA terms:', error)
    // Continue without auto-matching
  }
}

// DDA Terms Autocomplete Functions
const searchDDATerms = async (item, query) => {
  if (!query || query.length < 2) {
    item.searchResults = []
    item.showDropdown = false
    return
  }
  
  try {
    const response = await axios.get('/api/v1/dda_terms/search', {
      params: { q: query, limit: 20 }
    })
    
    item.searchResults = response.data || []
    item.showDropdown = true
  } catch (error) {
    console.error('Error searching DDA terms:', error)
    item.searchResults = []
    item.showDropdown = false
  }
}

const selectDDATermForItem = (item, term) => {
  item.dda_term_id = term.id
  item.searchTerm = term.term
  item.showDropdown = false
  item.searchResults = []
  item.selectedTerm = term
}

const getSelectedTermName = (termId) => {
  if (!termId) return 'Unknown Term'
  
  const classItem = classificationItems.value.find(i => i.dda_term_id == termId)
  if (classItem && classItem.selectedTerm && classItem.selectedTerm.term) {
    return classItem.selectedTerm.term
  }
  
  const term = ddaTerms.value.find(t => t.id == termId)
  if (term && term.term) {
    return term.term
  }
  
  return `Term ${termId}`
}

const clearSelection = (item) => {
  item.dda_term_id = null
  item.searchTerm = ''
  item.showDropdown = false
  item.searchResults = []
  item.selectedTerm = null
}

const handleFocus = (item) => {
  if (item.dda_term_id && item.searchTerm) {
    item.searchTerm = ''
  }
  
  if (item.searchResults && item.searchResults.length > 0) {
    item.showDropdown = true
  }
}

const handleBlur = (item) => {
  setTimeout(() => {
    item.showDropdown = false
    
    if (item.dda_term_id && (!item.searchTerm || item.searchTerm.trim() === '')) {
      const termName = getSelectedTermName(item.dda_term_id)
      if (termName !== 'Unknown Term') {
        item.searchTerm = termName
      }
    }
  }, 150)
}

const saveConfiguration = async () => {
  const mappingConfig = {
    excel_mapping: {
      header_row: selectedHeaderRow.value,
      data_start_row: selectedHeaderRow.value + 1,
      use_hierarchical_descriptions: useHierarchicalDescriptions.value,
      date_row: selectedDateRow.value,
      budget_mapping: {
        description_columns: excelMapping.value.description_columns,
        company_column: excelMapping.value.company_column,
        original_budget_column: excelMapping.value.original_budget_column,
        current_budget_column: excelMapping.value.current_budget_column,
        funded_at_closing_column: excelMapping.value.funded_at_closing_column,
        current_request_column: excelMapping.value.current_draw_column, // Map to backend expected name
        balance_to_complete_column: excelMapping.value.balance_to_complete_column,
        main_category_column: excelMapping.value.main_category_column,
        cost_code_column: excelMapping.value.cost_code_column
      },
      draw_columns: excelMapping.value.draw_columns,
      draw_period_labels: {},
      dda_term_mappings: classificationItems.value
        .filter(item => item.dda_term_id)
        .map(item => ({
          description: item.description,
          dda_term_id: item.dda_term_id
        }))
    }
  }
  
  try {
    // SAVE THE TEMPLATE CONFIG TO THE BACKEND
    const response = await axios.post(`/api/v1/project_imports/${props.projectImport.id}/configure_templates`, {
      template_config: mappingConfig
    })
    
    if (response.data.success) {
      console.log('Template configuration saved successfully!')
      emit('complete', mappingConfig)
    } else {
      throw new Error(response.data.error || 'Failed to save template configuration')
    }
  } catch (error) {
    console.error('Error saving template configuration:', error)
    alert('Error saving template configuration: ' + error.message)
  }
}

// Fallback methods for quick configuration
const testMapping = () => {
  alert('Test mapping functionality - would preview data extraction with current settings')
}

const saveQuickConfiguration = async () => {
  const mappingConfig = {
    excel_mapping: {
      header_row: quickConfig.value.header_row,
      data_start_row: quickConfig.value.data_row,
      budget_mapping: {
        description_columns: quickConfig.value.description_columns,
        current_budget_column: quickConfig.value.budget_column
      },
      draw_columns: [],
      draw_period_labels: {}
    }
  }
  
  try {
    // SAVE THE TEMPLATE CONFIG TO THE BACKEND
    const response = await axios.post(`/api/v1/project_imports/${props.projectImport.id}/configure_templates`, {
      template_config: mappingConfig
    })
    
    if (response.data.success) {
      console.log('Quick template configuration saved successfully!')
      emit('complete', mappingConfig)
    } else {
      throw new Error(response.data.error || 'Failed to save template configuration')
    }
  } catch (error) {
    console.error('Error saving quick template configuration:', error)
    alert('Error saving template configuration: ' + error.message)
  }
}

// Lifecycle
onMounted(() => {
  loadExcelData()
  loadDDATerms()
})
</script>

<style scoped lang="scss">
.excel-mapper-container {
  padding: 20px 0;
  width: 98vw;
  max-width: 2000px;
  height: 95vh;
  overflow-y: auto;
  margin: 0 auto;
}

/* Consistency Analysis Summary Banner */
.consistency-summary-banner {
  background: linear-gradient(135deg, #f8f9ff 0%, #e8f4ff 100%);
  border: 1px solid #d0e3ff;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;

  h4 {
    margin: 0 0 15px 0;
    color: #1a73e8;
    font-size: 16px;
  }

  .consistency-stats {
    display: flex;
    gap: 20px;
    margin-bottom: 15px;
  }

  .stat-item {
    flex: 1;
    padding: 12px;
    background: white;
    border-radius: 6px;
    text-align: center;
    border: 2px solid transparent;

    &.success {
      border-color: #4caf50;
      background: #f8fff9;
    }

    &.warning {
      border-color: #ff9800;
      background: #fff8f0;
    }

    .stat-number {
      display: block;
      font-size: 24px;
      font-weight: bold;
      color: #333;
    }

    .stat-label {
      display: block;
      font-size: 12px;
      color: #666;
      margin-top: 5px;
    }
  }

  .consistency-explanation {
    margin: 0;

    p {
      margin: 8px 0;
      font-size: 14px;
    }

    .warning-text {
      color: #e65100;
    }
  }
}

.mapping-info,
.sample-file-info {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 20px;
}

.loading-section {
  text-align: center;
  padding: 40px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4A5FD5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.excel-preview-fallback {
  margin-bottom: 20px;
  
  .placeholder-message {
    border: 2px dashed #ddd;
    padding: 40px;
    text-align: center;
    border-radius: 8px;
    margin-bottom: 20px;
    
    p {
      margin: 8px 0;
      color: #666;
      
      &:first-child {
        color: #ff9800;
        font-weight: bold;
      }
    }
  }
  
  .mapping-form {
    background: white;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    
    h4 {
      margin: 0 0 16px 0;
      color: #4A5FD5;
    }
    
    .form-row {
      display: flex;
      align-items: center;
      margin-bottom: 12px;
      
      label {
        width: 150px;
        font-weight: bold;
      }
      
      input {
        padding: 6px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        width: 200px;
      }
    }
  }
  
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
  }
}

.excel-config-section {
  margin-bottom: 20px;
}

.current-step {
  background: #e8f0fe;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  
  h4 {
    margin: 0 0 8px 0;
    color: #1a73e8;
  }
  
  p {
    margin: 0;
    color: #5f6368;
  }
}

.excel-preview {
  overflow-x: auto;
  margin-bottom: 20px;
}

.excel-preview {
  max-height: 600px;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 8px;
  margin: 16px 0;
}

.raw-excel-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  min-width: 800px;
  
  td {
    border: 1px solid #ddd;
    padding: 6px 8px;
    white-space: nowrap;
    cursor: pointer;
    min-width: 60px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    
    &:hover {
      background: #f5f5f5;
    }
  }
  
  .row-number {
    background: #f5f5f5;
    font-weight: bold;
    text-align: center;
    width: 40px;
    min-width: 40px;
    max-width: 40px;
    position: sticky;
    left: 0;
    z-index: 2;
  }
  
  .selected-header-row {
    background: #e3f2fd !important;
    
    td {
      font-weight: bold;
    }
  }
  
  .selected-date-row {
    background: #fff3e0 !important;
  }
  
  .clickable-row {
    cursor: pointer;
  }
}

.excel-cell {
  &.highlight-description {
    background: #e8f5e9 !important;
  }
  
  &.highlight-original-budget {
    background: #fff3e0 !important;
  }
  
  &.highlight-current-budget {
    background: #fce4ec !important;
  }
  
  &.highlight-current-draw {
    background: #fce4ec !important;
  }
  
  &.highlight-funded-at-closing {
    background: #e1f5fe !important;
  }
  
  &.highlight-balance-complete {
    background: #fff8e1 !important;
  }
  
  &.highlight-draw-columns {
    background: #f3e5f5 !important;
  }
  
  &.highlight-company {
    background: #f3e5f5 !important;
  }
  
  &.highlighted-draw-column {
    background: #e3f2fd !important;
  }
}

.structure-choice {
  .choice-options {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-top: 16px;
  }
  
  .choice-option {
    border: 2px solid #ddd;
    border-radius: 8px;
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s;
    
    &:hover {
      border-color: #4A5FD5;
    }
    
    &.selected {
      border-color: #4A5FD5;
      background: #f0f4ff;
    }
    
    h5 {
      margin: 0 0 8px 0;
      color: #333;
    }
    
    p {
      margin: 0 0 12px 0;
      color: #666;
    }
    
    .example {
      font-size: 12px;
      color: #888;
      background: #f5f5f5;
      padding: 8px;
      border-radius: 4px;
    }
  }
}

.date-row-info {
  background: #fff3e0;
  padding: 12px;
  border-radius: 4px;
  margin-top: 12px;
  
  p {
    margin: 4px 0;
  }
}

.dda-term-mapping-step {
  .dda-mapping-table-container {
    overflow-x: auto;
    max-height: 80vh;
    overflow-y: auto;
    margin-bottom: 20px;
    border: 1px solid #ddd;
    border-radius: 8px;
  }
  
  .dda-mapping-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    
    th, td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
    }
    
    th {
      background: #f5f5f5;
      font-weight: bold;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    
    .selected-row {
      background: #e3f2fd;
    }
    
    .dda-term-cell {
      min-width: 250px;
    }
    
    .description-cell {
      min-width: 200px;
    }
    
    .currency {
      text-align: right;
      white-space: nowrap;
    }
    
    .percent {
      text-align: center;
    }
  }
}

.dda-term-wrapper {
  position: relative;
  
  .auto-match-badge {
    position: absolute;
    top: -8px;
    right: -8px;
    background: #4CAF50;
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: bold;
  }
}

.dda-autocomplete {
  position: relative;
  
  .dda-search-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 12px;
    
    &:focus {
      outline: none;
      border-color: #4A5FD5;
    }
    
    &.has-selection {
      display: none;
    }
    
    &.auto-matched {
      background: #e8f5e9;
    }
  }
  
  .dda-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    max-height: 200px;
    overflow-y: auto;
    z-index: 100;
    
    .no-results {
      padding: 12px;
      text-align: center;
      color: #666;
    }
    
    .dda-option {
      padding: 8px 12px;
      cursor: pointer;
      border-bottom: 1px solid #f0f0f0;
      
      &:hover {
        background: #f5f5f5;
      }
      
      &.selected {
        background: #e3f2fd;
      }
      
      .term-name {
        font-weight: 500;
        color: #333;
      }
      
      .term-category {
        font-size: 11px;
        color: #666;
        margin-top: 2px;
      }
    }
  }
  
  .selected-term-display {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    background: #e3f2fd;
    border-radius: 4px;
    
    .selected-term-name {
      flex: 1;
      font-size: 12px;
      color: #1a73e8;
    }
    
    .clear-selection {
      background: none;
      border: none;
      color: #666;
      cursor: pointer;
      font-size: 18px;
      line-height: 1;
      padding: 0;
      
      &:hover {
        color: #333;
      }
    }
  }
}

.mapping-progress {
  margin-top: 20px;
  
  .progress-info {
    margin-bottom: 8px;
    font-size: 14px;
  }
  
  .progress-bar {
    height: 20px;
    background: #f0f0f0;
    border-radius: 10px;
    overflow: hidden;
    
    .progress-fill {
      height: 100%;
      background: #4CAF50;
      transition: width 0.3s ease;
    }
  }
}

.finalize-step {
  .mapping-summary {
    background: #f5f5f5;
    padding: 20px;
    border-radius: 8px;
    
    h4 {
      margin: 0 0 12px 0;
      color: #333;
    }
    
    .summary-list {
      margin-top: 16px;
      
      .summary-item {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-bottom: 1px solid #e0e0e0;
        
        &:last-child {
          border-bottom: none;
        }
        
        strong {
          color: #333;
        }
        
        .column-mapping {
          color: #666;
          
          &.mapped {
            color: #4CAF50;
            font-weight: 500;
          }
        }
      }
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.btn-primary,
.btn-secondary,
.btn-success {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s;
}

.btn-primary {
  background: #4A5FD5;
  color: white;
  
  &:hover {
    background: #3a4fb5;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

.btn-secondary {
  background: #f5f5f5;
  color: #333;
  border: 1px solid #ddd;
  
  &:hover {
    background: #e9e9e9;
  }
}

.btn-success {
  background: #4CAF50;
  color: white;
  
  &:hover {
    background: #45a049;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}
</style> 